/**
 * QuickLookToolbar — floating staff toolbar shown only during impersonation.
 *
 * Sprint 7 of the post-launch overhaul (Backlog #24). Adds three actions
 * to every `/pax/*` page when an impersonation session is active:
 *
 *   - Open this customer in the CRM (new tab)
 *   - Copy current URL as a magic-link share link
 *   - Create a Freshdesk ticket from the current view
 *
 * Hides itself for non-staff sessions (i.e. real pax users). The
 * ImpersonationBanner is already at the top of PaxApp; this toolbar lives
 * bottom-right so it doesn't fight with the banner for real-estate.
 */

import { useCallback, useState } from 'react';
import {
  Briefcase,
  Clipboard,
  ClipboardCheck,
  ExternalLink,
  MessageSquare,
  X,
} from 'lucide-react';
import { getImpersonateSession } from '../lib/impersonate';

const PLATFORM_DOMAIN = typeof window !== 'undefined' && window.location.hostname.includes('localhost')
  ? 'http://localhost:5173'
  : 'https://platform.propellertravel.com';

export function QuickLookToolbar() {
  const session = getImpersonateSession();
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const openInCrm = useCallback(() => {
    if (!session?.customerId) return;
    window.open(`${PLATFORM_DOMAIN}/customers/${session.customerId}`, '_blank', 'noopener,noreferrer');
  }, [session?.customerId]);

  const copyShareUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — no recovery */
    }
  }, []);

  const createFreshdeskTicket = useCallback(() => {
    if (!session?.customerEmail) return;
    const subject = encodeURIComponent(`Follow-up: ${window.document.title}`);
    const description = encodeURIComponent(
      `Customer: ${session.customerName ?? session.customerEmail}\nFrom: ${window.location.href}\n\n`,
    );
    // Freshdesk supports a `?subject=...&description=...&requester_id=...`
    // deep-link to the new-ticket form. We don't have the requester_id
    // mapping client-side, so we just pre-fill the body and let the
    // agent pick the requester via Freshdesk's contact search.
    const fdHost = 'https://proptravel.freshdesk.com/a/tickets/new';
    window.open(`${fdHost}?subject=${subject}&description=${description}`, '_blank', 'noopener,noreferrer');
  }, [session?.customerEmail, session?.customerName]);

  if (!session) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="fixed bottom-4 right-4 z-50 px-3 py-2 rounded-full bg-slate-900/90 hover:bg-slate-800 text-white text-xs font-mono uppercase tracking-wider shadow-xl"
      >
        Quick look
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 rounded-2xl bg-slate-900/95 backdrop-blur text-white shadow-2xl border border-slate-700 px-2 py-2 flex items-center gap-1">
      <div className="px-2 text-[10px] uppercase tracking-wider font-mono text-amber-300">
        Quick look
      </div>
      <span className="text-slate-500">·</span>
      <button
        type="button"
        onClick={openInCrm}
        className="px-2.5 py-1.5 rounded-lg hover:bg-slate-800 text-xs flex items-center gap-1.5"
        title="Open this customer's CRM record in a new tab"
      >
        <Briefcase className="w-3.5 h-3.5" />
        CRM
        <ExternalLink className="w-3 h-3 opacity-60" />
      </button>
      <button
        type="button"
        onClick={createFreshdeskTicket}
        className="px-2.5 py-1.5 rounded-lg hover:bg-slate-800 text-xs flex items-center gap-1.5"
        title="Open Freshdesk new-ticket form pre-filled with the current URL"
      >
        <MessageSquare className="w-3.5 h-3.5" />
        Ticket
        <ExternalLink className="w-3 h-3 opacity-60" />
      </button>
      <button
        type="button"
        onClick={copyShareUrl}
        className="px-2.5 py-1.5 rounded-lg hover:bg-slate-800 text-xs flex items-center gap-1.5"
        title="Copy this URL to share with the customer (won't include the impersonate token)"
      >
        {copied ? <ClipboardCheck className="w-3.5 h-3.5 text-emerald-400" /> : <Clipboard className="w-3.5 h-3.5" />}
        {copied ? 'Copied' : 'URL'}
      </button>
      <button
        type="button"
        onClick={() => setCollapsed(true)}
        className="ml-1 px-1.5 py-1 rounded-lg hover:bg-slate-800 text-slate-400"
        aria-label="Collapse"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
